import { Alert, Box, Button, LinearProgress, LinearProgressProps, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { forwardRef, useMemo } from "react";
import { useNavigate } from "react-router";
import Modal, { CommonModalProps } from "..";
import {
    GetCreationErrors,
    GetCreationProgress,
    GetErrorMessage,
    ResetReduxNewRequest,
} from "../../../Redux/Reducers/redux-slice-new-request";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { routePaths } from "../../../Router/router";
import DataTable, { CellSize } from "../../Table/table";
import { useTranslation } from "react-i18next";

export type ModalNewRequestBrokerElementProps = {};
export type ModalNewRequestBrokerProps = CommonModalProps<ModalNewRequestBrokerElementProps>;

const ModalNewRequestBroker = forwardRef<any, ModalNewRequestBrokerProps>((props, ref) => {
    const { t } = useTranslation();
    const { closeModal } = props;

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const creationProgress = useAppSelector(GetCreationProgress);
    const creationErrors = useAppSelector(GetCreationErrors);
    const errorMessage = useAppSelector(GetErrorMessage);

    const columns: GridColDef[] = useMemo(
        () => [
            {
                headerName: `${t("common:assetUpperCase")}  #`,
                field: "assetNumber",
                minWidth: CellSize.small,
                flex: CellSize.small,
                renderCell: (data) => (
                    <Box alignItems="center">
                        <Typography variant="body1">{data.value}</Typography>
                    </Box>
                ),
            },
            {
                headerName: t("common:placeholderName"),
                field: "placeholderName",
                minWidth: CellSize.small,
                flex: CellSize.small,
            },
            {
                headerName: t("common:upperCaseError"),
                field: "error",
                minWidth: CellSize.small,
                flex: CellSize.small,
            },
        ],
        []
    );

    const handleGoToRequestClick = () => {
        closeModal();
        navigate(routePaths.ingestRequested.path);
        dispatch(ResetReduxNewRequest());
    };

    return (
        <Modal
            reference={ref}
            title={t("modal:title.processingRequestsCreation")}
            closeModal={closeModal}
            width={800}
            footer={
                <Stack direction="row" justifyContent="space-between">
                    <Button variant="outlined" onClick={closeModal}>
                        {t("common:close")}
                    </Button>
                    <Button
                        onClick={handleGoToRequestClick}
                        variant="outlined"
                        color="success"
                        disabled={creationProgress.progress !== creationProgress.total}
                    >
                        {t("button:goToRequest")}
                    </Button>
                </Stack>
            }
        >
            <Stack spacing={2}>
                <Stack spacing={1}>
                    <Typography variant="h5">{t("common:progress")}</Typography>
                    {/* Check below to see if we could bring the whole scentence into translation file */}
                    <Typography variant="body1" fontWeight={500}>{`${creationProgress.progress}/${creationProgress.total} ${t(
                        "modal:body.assetsProcessed"
                    )}`}</Typography>
                    <LinearProgressWithLabel
                        color={
                            creationErrors.length > 0 || errorMessage.length > 0
                                ? "error"
                                : creationProgress.progress < creationProgress.total
                                    ? "primary"
                                    : "success"
                        }
                        variant="determinate"
                        value={(creationProgress.progress / creationProgress.total) * 100}
                    />
                    {creationProgress.progress < creationProgress.total && (
                        <Alert severity="warning">{t("alert:warning.canCloseModalAndNavigate")}</Alert>
                    )}
                    {creationErrors.length > 0 && creationProgress.progress === creationProgress.total && (
                        <Alert severity="error">{t("alert:error.errorDuringProcessing")}</Alert>
                    )}
                    {creationErrors.length === 0 &&
                        creationProgress.progress === creationProgress.total &&
                        errorMessage.length === 0 && <Alert severity="success">{t("alert:success.allRequestsCreated")}</Alert>}
                    {creationErrors.length === 0 &&
                        creationProgress.progress === creationProgress.total &&
                        errorMessage.length > 0 && <Alert severity="error">{errorMessage}</Alert>}
                </Stack>
                {creationErrors.length > 0 && (
                    <>
                        <Typography variant="h5">{t("common:errors")}</Typography>
                        <DataTable
                            noControls
                            columns={columns}
                            rows={creationErrors}
                            pagination={false}
                            disableColumnFilter
                            disableRowSelectionOnClick
                            initialState={{
                                rowGrouping: {
                                    model: ["assetNumber"],
                                },
                                pinnedColumns: { left: ["__check__"], right: ["actions"] },
                            }}
                        />
                    </>
                )}
            </Stack>
        </Modal>
    );
});

export default ModalNewRequestBroker;

const LinearProgressWithLabel = (props: LinearProgressProps) => {
    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ width: "100%", mr: 1 }}>
                <LinearProgress variant="determinate" {...props} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${Math.round(props.value || 0)}%`}</Typography>
            </Box>
        </Box>
    );
};
